import { Injectable } from '@angular/core';
import { SETTINGS } from '../config/common.settings';
import { HttpBackend, HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class MapboxService {
  private httpClient: HttpClient;

  constructor(handler: HttpBackend) {
    this.httpClient = new HttpClient(handler);
  }

  getMapImage(geoJson: any): Promise<any> {
    const geoJsonString = this.getEncodeURI(geoJson);
    const url = `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/geojson(${geoJsonString})/auto/1200x800?padding=40,40,40,40&access_token=${SETTINGS.MAP_BOX_ACCESS.TOKEN}`;
    return new Promise((resolve, reject) => {
      this.httpClient
        .get(url, {responseType: "blob"})
        .subscribe({
          next: (response: any) => {
            // Convert Blob to a base64 string
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64data = reader.result as string;
              resolve(base64data); // Return the base64 image URL
            };
            reader.onerror = (error) => {
              reject("Failed to read image blob.");
            };
            reader.readAsDataURL(response); // Convert blob to base64
          },
          error: (error: any) => {
            reject(error);
          },
        });
    });
  }

  // Compress only geometry (rounding + optional downsampling) without altering properties
  private compressGeometry(geometry: any): any {
    if (!geometry) { return geometry; }

    const round = (num: number) => Number(num.toFixed(5));

    const downsampleLine = (line: Array<number[]>): Array<number[]> => {
      if (!Array.isArray(line) || line.length <= 200) {
        return line.map((pt: number[]) => [round(pt[0]), round(pt[1])]);
      }
      const maxPoints = 200;
      const step = Math.ceil(line.length / maxPoints);
      const reduced: Array<number[]> = [];
      for (let i = 0; i < line.length; i += step) {
        const pt = line[i];
        reduced.push([round(pt[0]), round(pt[1])]);
      }
      const last = line[line.length - 1];
      const lastReduced = reduced[reduced.length - 1];
      if (!lastReduced || lastReduced[0] !== round(last[0]) || lastReduced[1] !== round(last[1])) {
        reduced.push([round(last[0]), round(last[1])]);
      }
      return reduced;
    };

    const compressCoordinates = (coords: any): any => {
      if (!coords) { return coords; }
      if (typeof coords[0] === "number") {
        return [round(coords[0]), round(coords[1])];
      }
      if (typeof coords[0][0] === "number") {
        return downsampleLine(coords as Array<number[]>);
      }
      return coords.map((c: any) => compressCoordinates(c));
    };

    if (geometry?.coordinates) {
      return {
        ...geometry,
        coordinates: compressCoordinates(geometry.coordinates)
      };
    }
    return geometry;
  }

  private getEncodeURI(geoJson: any): string {
    return encodeURIComponent(JSON.stringify({...geoJson}));
  }

  getStaticMapURL(geoJson: any): any {
    const geoJsonString = this.getEncodeURI(geoJson);
    return `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/geojson(${geoJsonString})/auto/800x600?access_token=${SETTINGS.MAP_BOX_ACCESS.TOKEN}`;
  }

  // New method to get directions route
  async getDirectionsRoute(waypoints: Array<{lat: number, lng: number}>): Promise<any> {

    const coordinates = waypoints.map(wp => `${wp.lng},${wp.lat}`).join(';');
    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${coordinates}?geometries=geojson&access_token=${SETTINGS.MAP_BOX_ACCESS.TOKEN}`;

    try {
      return await this.httpClient.get(url).toPromise();
    } catch (error) {
      console.error('Error fetching directions:', error);
      throw error;
    }
  }

  // New method to create static image with directions route
  async getStaticMapWithDirections(isStaticUrl: boolean, waypoints: Array<{lat: number, lng: number}>): Promise<any> {
    try {
      // First get the directions route
      const directionsResponse: any = await this.getDirectionsRoute(waypoints);

      // Create GeoJSON for the route
      const routeGeoJSON = {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: {
              "stroke": "#3b82f6",
              "stroke-width": 4,
              "stroke-opacity": 0.8
            },
            geometry: this.compressGeometry(directionsResponse.routes[0].geometry)
          },
          // Add markers for all waypoints
          ...waypoints.map((wp: any, index: number) => ({
            type: "Feature",
            properties: {
              "marker-size": "medium",
              "marker-color":
                index === 0
                  ? "#10b981"
                  : index === waypoints.length - 1
                    ? "#ef4444"
                    : "#f59e0b"
            },
            geometry: {
              type: "Point",
              coordinates: [wp.lng, wp.lat]
            }
          }))
        ]
      };

      // Get static image with the route
      if (isStaticUrl) {
       return this.getStaticMapURL(routeGeoJSON);
      }else{
        return await this.getMapImage(routeGeoJSON);
      }
    } catch (error) {
    }
  }
}
